/**
 * Brown Butters — "Explore fragrances" carousel.
 *
 * The scrolling itself is pure CSS (overflow + scroll-snap), so the carousel is usable
 * with JavaScript disabled. This module only adds the three things CSS cannot do:
 *
 *   1. Circular prev/next buttons that advance one card at a time and wrap around.
 *   2. Marking the card nearest the viewport centre as active, so it can be enlarged.
 *   3. Optional autoplay, which yields to `prefers-reduced-motion` and to any shopper
 *      who is hovering, touching or tabbing through the carousel.
 *
 * Dependency-free on purpose: it only talks to the DOM, so it never has to be kept in
 * step with the theme's internal module graph.
 */

const ACTIVE_CLASS = 'is-active';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

class MbbFragranceCarousel extends HTMLElement {
  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number} */
  #frame = 0;

  /** @type {number} */
  #timer = 0;

  /** @type {boolean} */
  #paused = false;

  /** @type {boolean} */
  #looping = false;

  /** Number of real (non-clone) slides. */
  #realCount = 0;

  connectedCallback() {
    const track = this.track;
    if (!track) return;

    const { signal } = this.#abortController;

    track.addEventListener('scroll', this.#requestSync, { passive: true, signal });
    track.addEventListener('keydown', this.#handleKeydown, { signal });
    this.addEventListener('pointerenter', this.#pause, { signal });
    this.addEventListener('pointerleave', this.#resume, { signal });
    this.addEventListener('focusin', this.#pause, { signal });
    this.addEventListener('focusout', this.#resume, { signal });
    document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });

    const previousButton = this.querySelector('[data-mbb-frag-prev]');
    const nextButton = this.querySelector('[data-mbb-frag-next]');
    previousButton?.addEventListener('click', this.#previous, { signal });
    nextButton?.addEventListener('click', this.#next, { signal });

    if (typeof ResizeObserver === 'function') {
      this.#resizeObserver = new ResizeObserver(this.#handleResize);
      this.#resizeObserver.observe(track);
    }

    // read before cloning — initialIndex is derived from slides.length, which triples
    const startIndex = this.initialIndex;
    this.#buildLoop();
    // start in the middle set so there is a full copy to travel through either way
    this.#scrollToIndex(startIndex + this.#realCount, false);
    this.#paintScale();
    this.#sync();
    this.#startAutoplay();

    /*
      Re-centre once the row has its final width.

      connectedCallback runs before the flavour artwork has loaded, so the first
      scrollToIndex aimed at a layout that then changed underneath it — the track settled
      115px off, almost exactly half a card, and nothing ever corrected it. The centred card
      therefore sat permanently between two snap points: its proximity peaked at ~0.53, so it
      only ever grew to 1.25x instead of the design's 1.46x, and the carousel looked like it
      had stopped enlarging the middle card.

      Two rAFs covers the normal relayout; the image load listeners cover artwork that
      arrives later. Both re-centre on #keepCentred, which #scrollToIndex keeps up to date,
      so this can never yank the row away from a card the shopper chose.
    */
    requestAnimationFrame(() => requestAnimationFrame(this.#recentre));
    for (const img of this.querySelectorAll('img')) {
      if (!img.complete) img.addEventListener('load', this.#recentre, { once: true, signal });
    }
  }

  /** Index that should stay centred across relayouts. Updated by every deliberate move. */
  #keepCentred = -1;

  #recentre = () => {
    if (this.#keepCentred < 0) return;
    this.#scrollToIndex(this.#keepCentred, false);
    this.#paintScale();
  };

  #handleResize = () => {
    this.#recentre();
    this.#requestSync();
  };

  /**
   * Continuous, cyclic scrolling.
   *
   * The index maths already wrapped, but the scroll did not: stepping past the last card
   * ran `scrollTo` all the way back to the start, so the row visibly rewound instead of
   * carrying on. Cloning the set either side gives three copies and lets the shopper keep
   * going in both directions; #wrapScroll then shifts the scroll position by exactly one
   * set whenever it leaves the middle one, which is invisible because the pixels under
   * the pointer are identical.
   *
   * Clones are inert: hidden from assistive tech and out of the tab order, so the same
   * flavour is never announced or focused three times.
   */
  #buildLoop() {
    const track = this.track;
    if (!track) return;
    const originals = this.slides;
    this.#realCount = originals.length;
    if (originals.length < 2) return;

    const clone = (slide) => {
      const copy = slide.cloneNode(true);
      copy.setAttribute('data-mbb-frag-clone', '');
      copy.setAttribute('aria-hidden', 'true');
      copy.querySelectorAll('a, button, [tabindex]').forEach((el) => el.setAttribute('tabindex', '-1'));
      copy.removeAttribute('id');
      copy.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      return copy;
    };

    // the slides are not direct children of the track (there is a list in between), so
    // clone into their real parent — inserting into the track itself throws NotFoundError
    const container = originals[0].parentElement;
    if (!container) return;

    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();
    for (const slide of originals) {
      before.appendChild(clone(slide));
      after.appendChild(clone(slide));
    }
    container.insertBefore(before, originals[0]);
    container.appendChild(after);
    this.#looping = true;
  }

  /** Keep the scroll position inside the middle set so it can never run out of track. */
  #wrapScroll() {
    if (!this.#looping) return;
    const track = this.track;
    if (!track) return;
    const setWidth = track.scrollWidth / 3;
    if (setWidth <= 0) return;

    if (track.scrollLeft < setWidth * 0.5) {
      track.scrollLeft += setWidth;
    } else if (track.scrollLeft >= setWidth * 2.5) {
      track.scrollLeft -= setWidth;
    }
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#abortController = new AbortController();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    if (this.#frame) cancelAnimationFrame(this.#frame);
    if (this.#scaleFrame) cancelAnimationFrame(this.#scaleFrame);
    this.#scaleFrame = 0;
    this.#frame = 0;
    clearTimeout(this.#settleTimer);
    this.#settleTimer = 0;
    this.#stopAutoplay();
  }

  /** The horizontally scrolling element. */
  get track() {
    return this.querySelector('[data-mbb-frag-track]');
  }

  /** @returns {HTMLElement[]} */
  get slides() {
    return Array.from(this.querySelectorAll('[data-mbb-frag-slide]'));
  }

  /** Card that should be centred on first paint — the middle one, as in the design. */
  get initialIndex() {
    const requested = Number.parseInt(this.dataset.initialIndex ?? '', 10);
    if (Number.isFinite(requested)) return requested;
    return Math.floor(Math.max(this.slides.length - 1, 0) / 2);
  }

  get autoplayEnabled() {
    return this.dataset.autoplay === 'true' && !window.matchMedia(REDUCED_MOTION_QUERY).matches;
  }

  /** Autoplay interval in milliseconds. */
  get autoplaySpeed() {
    const seconds = Number.parseFloat(this.dataset.autoplaySpeed ?? '');
    return (Number.isFinite(seconds) && seconds > 0 ? seconds : 5) * 1000;
  }

  /** Index of the card whose centre is closest to the centre of the viewport. */
  get activeIndex() {
    const track = this.track;
    const slides = this.slides;
    if (!track || slides.length === 0) return 0;

    /*
      Compare CENTRES, measured with rects.

      A rect's centre is immune to the card's scale because transform-origin is `bottom
      center` — scaling widens the box symmetrically and leaves its midpoint exactly where it
      was. Widths are not immune, so nothing here may use one.

      offsetLeft is the wrong tool despite being untransformed: it is relative to
      offsetParent, which is not the track, so mixing it with track.scrollLeft compares two
      different coordinate systems. That constant offset is what left the row parked half a
      card off centre.
    */
    const metrics = this.#metrics();
    if (!metrics) return 0;
    const centre = track.scrollLeft + track.clientWidth / 2;

    let best = 0;
    let bestDistance = Number.POSITIVE_INFINITY;

    metrics.items.forEach((it, index) => {
      const distance = Math.abs(it.centre - centre);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });

    return best;
  }

  /*
    Settle before re-picking the active card.

    Marking the active card changes its flex-basis, which is a LAYOUT change: every card
    in the row shifts sideways. Doing that on each scroll frame meant the shift moved a
    different card nearest to centre, which re-marked, which shifted again — a feedback
    loop that reads as jitter while you scroll.

    The wrap still runs every frame (the loop must never run out of track), but the active
    card is only recomputed once movement stops, so the row resizes exactly once per
    gesture instead of continuously.
  */
  #settleTimer = 0;

  /** @type {number} */
  #scaleFrame = 0;

  #requestSync = () => {
    this.#wrapScroll();
    // scale tracks the finger, every frame
    if (!this.#scaleFrame) this.#scaleFrame = requestAnimationFrame(this.#paintScale);
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(() => this.#sync(), 140);
  };

  /**
   * Continuous size: each card gets --mbb-frag-p, 1 at dead centre falling linearly to 0 one
   * card-step away, and CSS turns that into a scale. So a card grows as it travels and is
   * full size exactly as it arrives, instead of popping once the scroll has stopped.
   *
   * Measured from rect CENTRES. transform-origin is `bottom center`, so scaling never moves
   * a card's midpoint — the centre is a fixed point of the transform and therefore safe to
   * feed back in. Widths are NOT safe and none are read. offsetLeft looks safer but is
   * relative to offsetParent rather than the track, which silently mixed coordinate systems
   * and biased every card by half a step.
   */
  /**
   * Layout geometry of the row: box width, gap, and the centre-to-centre step.
   * Read from the layout box only — never from a rect, which carries this component's own
   * scale and translation and would feed its output back into its input.
   */
  /**
   * Per-slide geometry in the track's scroll space.
   *
   * Slides used to be uniform, so one width and one step described the whole row. They now
   * size to their labels ("Caramel Coffee" is wider than "Coconut"), so each one needs its
   * own centre. Positions come from offsetLeft MINUS the list's offsetLeft: both share an
   * offsetParent, so the difference is the position inside the list — which is the scroll
   * space — and neither is affected by the transforms this component writes.
   */
  #metrics() {
    const slides = this.slides;
    if (!slides.length) return null;
    const list = slides[0].parentElement;
    if (!list) return null;
    const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
    const base = list.offsetLeft;
    const items = slides.map((s) => ({
      left: s.offsetLeft - base,
      width: s.offsetWidth,
      centre: s.offsetLeft - base + s.offsetWidth / 2,
    }));
    return { items, gap };
  }

  #paintScale = () => {
    this.#scaleFrame = 0;
    const track = this.track;
    if (!track) return;

    const slides = this.slides;
    if (!slides.length) return;

    /*
      Positions come from an INDEX MODEL, not from measurement.

      Every layout box is the same width w with the same gap g between them, so card i sits
      at `i * (w + g) + w / 2` in the track's scroll space. That is a pure function of the
      scroll offset and two constants — which matters because this method also TRANSLATES the
      cards. Measuring rects here would mean reading back a position this very method had
      just written, and the row would walk away from itself frame by frame.
    */
    const metrics = this.#metrics();
    if (!metrics) return;
    const { items, gap } = metrics;

    const viewportCentre = track.scrollLeft + track.clientWidth / 2;
    const scale = parseFloat(getComputedStyle(this).getPropertyValue('--mbb-frag-active')) || 1.46;

    // proximity: 1 dead centre, 0 once a whole card-plus-gap away. The reach is that card's
    // own width, so a wide tile does not fall off faster than a narrow one.
    const ps = items.map((it) => {
      const reach = it.width + gap;
      const distance = Math.abs(it.centre - viewportCentre) / reach;
      if (distance >= 1) return 0;
      /*
        Smoothstep, not a linear ramp. `1 - distance` has a corner at both ends: a
        card jumps to full growth rate the moment it comes into range and stops dead
        on arrival, which reads as abrupt and makes the row feel like it snaps. This
        curve is flat at both ends and steepest in the middle, so growth eases in and
        settles rather than stopping.
      */
      const t = 1 - distance;
      return t * t * (3 - 2 * t);
    });

    const extras = ps.map((p, i) => (scale - 1) * items[i].width * p);
    const total = extras.reduce((sum, value) => sum + value, 0);

    /*
      Write only what changed.

      This runs every frame while scrolling and there are 21 slides once the loop clones are
      in, so a naive pass sets 42 custom properties per frame — and because each one feeds a
      transform on a descendant, every write invalidates that card's subtree style. It
      measured fine on a fast machine and is exactly the kind of thing that drops frames on a
      phone. Most cards are far from the centre and hold the same two values for seconds at a
      time, so comparing against the last written value skips almost all of it.
    */
    let before = 0;
    slides.forEach((slide, i) => {
      const shift = before + extras[i] / 2 - total / 2;
      before += extras[i];

      const p = ps[i].toFixed(3);
      const x = shift.toFixed(1);
      if (slide.dataset.fragP !== p) {
        slide.dataset.fragP = p;
        slide.style.setProperty('--mbb-frag-p', p);
      }
      if (slide.dataset.fragX !== x) {
        slide.dataset.fragX = x;
        slide.style.setProperty('--mbb-frag-x', `${x}px`);
      }
    });
  };

  /** Reflect the current scroll position onto the cards. */
  #sync() {
    const active = this.activeIndex;
    // a settled gesture becomes the new thing to hold centred through later relayouts
    this.#keepCentred = active;
    const previous = this.slides.findIndex((s) => s.classList.contains(ACTIVE_CLASS));

    this.slides.forEach((slide, index) => {
      const isActive = index === active;
      slide.classList.toggle(ACTIVE_CLASS, isActive);
      const link = slide.querySelector('a');
      if (link) {
        if (isActive) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      }
    });

    /*
      The forced re-centre that used to live here is gone. It existed because growing the
      active card reflowed the row out from under the browser's snap position. Size is a
      transform now, so the layout never moves and the snap target stays where the browser
      put it — correcting it would only fight the user's own scroll.
    */
    void previous;
  }

  /**
   * @param {number} index
   * @param {boolean} smooth
   */
  #scrollToIndex(index, smooth = true) {
    const track = this.track;
    const slides = this.slides;
    if (!track || slides.length === 0) return;

    const clamped = ((index % slides.length) + slides.length) % slides.length;
    const slide = slides[clamped];
    if (!slide) return;
    this.#keepCentred = clamped;

    /*
      Same reason as activeIndex: measure the layout box, not the painted one.

      With getBoundingClientRect the target was computed from the SCALED width, so the card
      never landed dead centre — it settled about half a step short, which meant its
      proximity value peaked at ~0.53 and it only ever grew to 1.25x instead of the design's
      1.46x. The carousel looked like it had stopped enlarging the centre card at all.
    */
    // Same per-slide model as activeIndex and #paintScale — one coordinate system for all three.
    const metrics = this.#metrics();
    if (!metrics || !metrics.items[clamped]) return;
    const left = metrics.items[clamped].centre - track.clientWidth / 2;

    const reduced = window.matchMedia(REDUCED_MOTION_QUERY).matches;
    track.scrollTo({ left, behavior: smooth && !reduced ? 'smooth' : 'auto' });
  }

  /** @param {number} direction */
  #step(direction) {
    this.#scrollToIndex(this.activeIndex + direction);
  }

  #previous = () => {
    this.#step(-1);
  };

  #next = () => {
    this.#step(1);
  };

  /**
   * Only drive the carousel when the scroll container itself has focus, so arrow keys
   * still behave normally once a shopper has tabbed onto one of the cards.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.target !== this.track) return;

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#step(1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#step(-1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.#scrollToIndex(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      this.#scrollToIndex(this.slides.length - 1);
    }
  };

  /** Bring the block a merchant just selected in the theme editor into view. */
  #handleBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !this.contains(target)) return;
    const slide = target.closest('[data-mbb-frag-slide]');
    if (!slide) return;
    this.#scrollToIndex(this.slides.indexOf(slide));
  };

  #pause = () => {
    this.#paused = true;
  };

  #resume = () => {
    this.#paused = false;
  };

  #startAutoplay() {
    this.#stopAutoplay();
    if (!this.autoplayEnabled || this.slides.length < 2) return;
    this.#timer = setInterval(() => {
      if (this.#paused || document.hidden) return;
      this.#step(1);
    }, this.autoplaySpeed);
  }

  #stopAutoplay() {
    if (this.#timer) clearInterval(this.#timer);
    this.#timer = 0;
  }
}

if (!customElements.get('mbb-fragrance-carousel')) {
  customElements.define('mbb-fragrance-carousel', MbbFragranceCarousel);
}
